import * as React from 'react';
import { useNavigate, useRouterState } from '@tanstack/react-router';

export interface SalesFilters {
  from: string;
  to: string;
  /** Source DB name (= store identity). E.g. "DALEEPNEW" or "DALEEP_20260501". */
  store: string | null;
  page: number;
}

const LS_KEY = 'daleep:salesFilters:v2';

interface PersistedShape {
  from?: string;
  to?: string;
  store?: string | null;
}

function readPersisted(): PersistedShape {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as PersistedShape;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
}

function writePersisted(p: PersistedShape) {
  if (typeof window === 'undefined') return;
  try {
    const cur = readPersisted();
    window.localStorage.setItem(LS_KEY, JSON.stringify({ ...cur, ...p }));
  } catch {
    /* quota / privacy mode — ignore */
  }
}

function defaultRange(): { from: string; to: string } {
  const today = new Date();
  // Default to first-of-month → 2 days ago (d-2 onward is MV-cached).
  // d-0/d-1 hit live MSSQL which can be slow; avoid on first load.
  const twoDaysAgo = new Date(today);
  twoDaysAgo.setDate(twoDaysAgo.getDate() - 2);
  const first = new Date(today.getFullYear(), today.getMonth(), 1);
  // If we're in the first 2 days of the month, use previous month
  const effectiveTo = twoDaysAgo < first
    ? new Date(first.getTime() - 1)  // last day of prev month
    : twoDaysAgo;
  const effectiveFrom = twoDaysAgo < first
    ? new Date(first.getFullYear(), first.getMonth() - 1, 1)
    : first;
  const f = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  return { from: f(effectiveFrom), to: f(effectiveTo) };
}

const moduleDefault = defaultRange();

export function useSalesFilters() {
  const search = useRouterState({
    select: (s) => s.location.search as { from?: string; to?: string; store?: string; page?: number },
  });
  const nav = useNavigate();

  // Resolution order: URL → localStorage → module default.
  const persisted = React.useMemo(readPersisted, []);

  const filters: SalesFilters = {
    from: search.from ?? persisted.from ?? moduleDefault.from,
    to: search.to ?? persisted.to ?? moduleDefault.to,
    store: search.store ?? persisted.store ?? null,
    page: search.page ?? 1,
  };

  // One-shot URL hydration on mount when URL is bare but persistence has values.
  const hydratedRef = React.useRef(false);
  React.useEffect(() => {
    if (hydratedRef.current) return;
    hydratedRef.current = true;
    const needsHydrate =
      search.from === undefined && search.to === undefined && search.store === undefined &&
      (persisted.from || persisted.to || persisted.store);
    if (!needsHydrate) return;
    nav({
      to: '.',
      search: (prev) => {
        const next: Record<string, unknown> = { ...(prev as Record<string, unknown>) };
        if (persisted.from) next.from = persisted.from;
        if (persisted.to) next.to = persisted.to;
        if (persisted.store) next.store = persisted.store;
        return next;
      },
      replace: true,
    });
  }, [nav, search.from, search.to, search.store, persisted]);

  const setFilters = React.useCallback((patch: Partial<SalesFilters>) => {
    const persistPatch: PersistedShape = {};
    if (patch.from !== undefined) persistPatch.from = patch.from;
    if (patch.to !== undefined) persistPatch.to = patch.to;
    if (patch.store !== undefined) persistPatch.store = patch.store;
    if (Object.keys(persistPatch).length > 0) writePersisted(persistPatch);

    nav({
      to: '.',
      search: (prev) => {
        const next: Record<string, unknown> = { ...(prev as Record<string, unknown>) };
        if (patch.from !== undefined) next.from = patch.from;
        if (patch.to !== undefined) next.to = patch.to;
        if (patch.store !== undefined) {
          if (patch.store === null) delete next.store;
          else next.store = patch.store;
        }
        if (patch.page !== undefined) {
          if (patch.page <= 1) delete next.page;
          else next.page = patch.page;
        }
        return next;
      },
      replace: true,
    });
  }, [nav]);

  return { ...filters, setFilters };
}
